import { useCallback, useEffect, useRef, useState } from 'react';
import { tagsFilterDropdownMotionDurationMs } from './tagsFilterDropdownMotion';

type UseTagsFilterDropdownOpenInput = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

type UseTagsFilterDropdownOpenResult = {
  isDropdownOpen: boolean;
  isDropdownClosing: boolean;
  shouldRenderDropdown: boolean;
  setDropdownOpen: (nextOpen: boolean) => void;
};

export const useTagsFilterDropdownOpen = ({
  open,
  onOpenChange,
}: UseTagsFilterDropdownOpenInput): UseTagsFilterDropdownOpenResult => {
  const [internalDropdownOpen, setInternalDropdownOpen] = useState(false);
  const [isDropdownClosing, setDropdownClosing] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousOpenRef = useRef(open ?? internalDropdownOpen);
  const isDropdownOpen = open ?? internalDropdownOpen;

  const clearCloseTimer = useCallback((): void => {
    if (closeTimerRef.current === null) return;
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);

  const startClosing = useCallback((): void => {
    clearCloseTimer();
    setDropdownClosing(true);
    closeTimerRef.current = setTimeout(() => {
      setDropdownClosing(false);
      closeTimerRef.current = null;
    }, tagsFilterDropdownMotionDurationMs);
  }, [clearCloseTimer]);

  const setDropdownOpen = useCallback(
    (nextOpen: boolean): void => {
      if (nextOpen) {
        clearCloseTimer();
        setDropdownClosing(false);
      } else if (isDropdownOpen) {
        startClosing();
      }
      if (open === undefined) {
        setInternalDropdownOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [clearCloseTimer, isDropdownOpen, onOpenChange, open, startClosing],
  );

  useEffect(() => {
    if (isDropdownOpen) {
      clearCloseTimer();
      setDropdownClosing(false);
    } else if (previousOpenRef.current && !isDropdownClosing) {
      startClosing();
    }
    previousOpenRef.current = isDropdownOpen;
  }, [clearCloseTimer, isDropdownClosing, isDropdownOpen, startClosing]);

  useEffect(() => clearCloseTimer, [clearCloseTimer]);

  return {
    isDropdownOpen,
    isDropdownClosing,
    shouldRenderDropdown: isDropdownOpen || isDropdownClosing,
    setDropdownOpen,
  };
};
